//@ts-nocheck
import { api } from "../../services/api";
import { Message } from "../pages/homepage/RightPanel";
import { openDB } from "idb";

const DB_NAME = "ChatAppDB";
const MESSAGE_STORE = "messages";
const KEY_STORE = "privateKeys";
const PUBLIC_KEY_STORE = "publicKeys";
const STORE_NAME = "keys";

const MESSAGE_LIMIT = 100;

// ==================== INSTÂNCIA COMPARTILHADA DO BANCO ====================
let dbInstance: Promise<IDBDatabase> | null = null;

const getDB = async (): Promise<IDBDatabase> => {
  if (dbInstance) {
    return dbInstance;
  }

  dbInstance = openDB(DB_NAME, 4, {
    upgrade(db, oldVersion) {
      if (!db.objectStoreNames.contains(MESSAGE_STORE)) {
        const messagesStore = db.createObjectStore(MESSAGE_STORE, { keyPath: "userId" });
        messagesStore.createIndex("chatId", "chatId", { unique: false });
      }

      if (!db.objectStoreNames.contains(KEY_STORE)) {
        db.createObjectStore(KEY_STORE, { keyPath: "userId" });
      }

      if (!db.objectStoreNames.contains("groups")) {
        const groupStore = db.createObjectStore("groups", { keyPath: "id" });
        groupStore.createIndex("id", "id", { unique: true });
      }

      if (!db.objectStoreNames.contains(PUBLIC_KEY_STORE)) {
        db.createObjectStore(PUBLIC_KEY_STORE, { keyPath: "userId" });
      }

      if (!db.objectStoreNames.contains("toggles")) {
        const toggleStore = db.createObjectStore("toggles", { keyPath: "key" });
        toggleStore.createIndex("by_key", "key", { unique: true });
      }
    },
  });

  return dbInstance;
};

// ==================== FUNÇÕES ORIGINAIS OTIMIZADAS ====================

// Função genérica para salvar no IndexedDB
export const indexedDBSet = async (key: string, value: any) => {
  const db = await getDB();
  await db.put(STORE_NAME, value, key);
};

// Função genérica para obter dados do IndexedDB
export const indexedDBGet = async (key: string) => {
  const db = await getDB();
  return db.get(STORE_NAME, key);
};

export const saveMessageKey = async (iv: string, key: any) => {
  const db = await getDB();
  await db.put(STORE_NAME, { iv, key });
};

export const getMessageKey = async (iv: string): Promise<CryptoKey | undefined> => {
  const db = await getDB();
  const response = await db.get(STORE_NAME, iv);
  return response?.key;
};

// Salvar chave pública
export const savePublicKeyToIndexedDB = async (userId: string, publicKey: CryptoKey) => {
  try {
    const db = await getDB();
    await db.put(PUBLIC_KEY_STORE, { userId, publicKey });
  } catch (error) {
    console.error("Erro ao salvar chave pública no IndexedDB", error);
  }
};

export const deleteChatMessagesFromDB = async (loggedUserId: string, chatIdToDelete: string): Promise<void> => {
  try {
    const db = await getDB();
    const store = db.transaction(MESSAGE_STORE, "readwrite").objectStore(MESSAGE_STORE);

    const existingData = await store.get(loggedUserId);

    if (existingData && existingData.messages) {
      if (chatIdToDelete in existingData.messages) {
        delete existingData.messages[chatIdToDelete];
        await store.put(existingData);
      }
    }
  } catch (error) {
    console.error("Erro ao apagar mensagens do chat no IndexedDB:", error);
    throw error;
  }
};

// Obter chave pública
export const getPublicKeyFromIndexedDB = async (userId: string): Promise<CryptoKey | undefined> => {
  try {
    const db = await getDB();
    const record = await db.get(PUBLIC_KEY_STORE, userId);
    return record?.publicKey;
  } catch (error) {
    console.error("Erro ao carregar chave pública do IndexedDB", error);
    return undefined;
  }
};

// Salvar chave privada
export const savePrivateKeyToIndexedDB = async (userId: string, privateKey: CryptoKey) => {
  try {
    const db = await getDB();
    await db.put(KEY_STORE, { userId, privateKey });
  } catch (error) {
    console.error("Erro ao salvar chave privada no IndexedDB", error);
  }
};

// Carregar chave privada
export const loadPrivateKeyFromIndexedDB = async (userId: string): Promise<CryptoKey | null> => {
  try {
    const db = await getDB();
    const record = await db.get(KEY_STORE, userId);
    return record?.privateKey || null;
  } catch (error) {
    console.error("Erro ao carregar chave privada do IndexedDB", error);
    return null;
  }
};

// Salvar mensagem criptografada
export const saveMessageToIndexedDB = async ({
  messages,
  userId,
}: {
  messages: Record<string, Message[]>;
  userId: string;
}) => {
  try {
    const db = await getDB();
    const store = db.transaction(MESSAGE_STORE, "readwrite").objectStore(MESSAGE_STORE);

    const existingData = await store.get(userId);

    if (existingData) {
      existingData.messages = {
        ...existingData.messages,
        ...messages,
      };
      await store.put(existingData);
    } else {
      await store.add({ messages, userId });
    }
  } catch (error) {
    console.error("Erro ao salvar mensagem no IndexedDB:", error);
  }
};

export const saveMessagesBatchToIndexedDB = async ({
  messagesByChat,
  userId,
}: {
  messagesByChat: Record<string, Message[]>; // Ex: { 'chatId1': [msg1, msg2], 'chatId2': [msg3] }
  userId: string;
}) => {
  try {
    const db = await getDB();
    const store = db.transaction(MESSAGE_STORE, "readwrite").objectStore(MESSAGE_STORE);

    const existingData = await store.get(userId);

    if (existingData) {
      // Itera sobre cada chat ID no lote
      for (const chatId in messagesByChat) {
        if (Object.prototype.hasOwnProperty.call(messagesByChat, chatId)) {
          const newMessages = messagesByChat[chatId];
          // Garante que o array de mensagens para este chat existe
          if (!existingData.messages[chatId]) {
            existingData.messages[chatId] = [];
          }
          // Adiciona as novas mensagens ao chat existente
          existingData.messages[chatId].push(...newMessages);
        }
      }
      await store.put(existingData);
    } else {
      // Se não há dados existentes, cria um novo registro
      const initialData = {
        userId,
        messages: messagesByChat,
      };
      await store.add(initialData);
    }
  } catch (error) {
    console.error("Erro ao salvar lote de mensagens no IndexedDB:", error);
  }
};

export const editMessageInIndexedDB = async ({
  userId,
  messageId,
  newContent,
}: {
  userId: string;
  messageId: string;
  newContent: Partial<Message>;
}) => {
  try {
    const db = await getDB();
    const store = db.transaction(MESSAGE_STORE, "readwrite").objectStore(MESSAGE_STORE);

    const existingData = await store.get(userId);

    if (existingData && existingData.messages) {
      let messageEdited = false;

      for (const senderId in existingData.messages) {
        const messagesArray = existingData.messages[senderId];

        if (Array.isArray(messagesArray)) {
          const messageIndex = messagesArray.findIndex((msg: any) => msg.id === messageId);

          if (messageIndex !== -1) {
            messagesArray[messageIndex] = {
              ...messagesArray[messageIndex],
              ...newContent,
            };
            messageEdited = true;
            break;
          }
        }
      }

      if (messageEdited) {
        await store.put(existingData);
      }
    }
  } catch (error) {
    console.error("Erro ao editar mensagem no IndexedDB:", error);
  }
};

export const deleteAllInformationsFromUserId = async (userIdLogged: string, userId: string) => {
  try {
    const db = await getDB();
    const tx = db.transaction([MESSAGE_STORE, PUBLIC_KEY_STORE, KEY_STORE], "readwrite");

    const messageStore = tx.objectStore(MESSAGE_STORE);
    const publicKeyStore = tx.objectStore(PUBLIC_KEY_STORE);
    const privateKeyStore = tx.objectStore(KEY_STORE);

    const existingData = await messageStore.get(userIdLogged);

    if (existingData && existingData.messages) {
      let changed = false;

      for (const senderId in existingData.messages) {
        const originalMessages = existingData.messages[senderId];
        const filteredMessages = originalMessages.filter((msg: Message) => msg.senderId !== userId);

        if (filteredMessages.length !== originalMessages.length) {
          changed = true;

          if (filteredMessages.length > 0) {
            existingData.messages[senderId] = filteredMessages;
          } else {
            delete existingData.messages[senderId];
          }
        }
      }

      if (changed) {
        await messageStore.put(existingData);
      }
    }

    await Promise.all([publicKeyStore.delete(userId), privateKeyStore.delete(userId)]);

    await tx.done;
  } catch (error) {
    console.error("Erro ao remover os dados do usuário no IndexedDB:", error);
  }
};

export const getAllIndexedDBData = (dbName: string): Promise<Record<string, any[]>> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName);

    request.onerror = (event) => {
      reject(`Erro ao abrir o banco: ${request.error}`);
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(db.objectStoreNames, "readonly");

      const result: Record<string, any[]> = {};
      let pendingStores = db.objectStoreNames.length;

      const handleStoreResult = (storeName: string, getAllRequest: IDBRequest) => {
        getAllRequest.onsuccess = () => {
          result[storeName] = getAllRequest.result;
          pendingStores--;

          if (pendingStores === 0) {
            resolve(result);
          }
        };

        getAllRequest.onerror = () => {
          reject(`Erro ao ler store "${storeName}": ${getAllRequest.error}`);
        };
      };

      for (const storeName of db.objectStoreNames) {
        const store = transaction.objectStore(storeName);
        const getAllRequest = store.getAll();
        handleStoreResult(storeName, getAllRequest);
      }
    };
  });
};

export const importarDadosIndexedDB = async (dbName: string, dados: Record<string, any[]>): Promise<string> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName);

    request.onerror = () => {
      reject(`Erro ao abrir o banco: ${request.error}`);
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(db.objectStoreNames, "readwrite");

      transaction.onerror = () => {
        reject(`Erro na transação: ${transaction.error}`);
      };

      for (const storeName of db.objectStoreNames) {
        const store = transaction.objectStore(storeName);
        const dadosStore = dados[storeName] || [];

        store.clear();

        for (const item of dadosStore) {
          store.put(item);
        }
      }

      transaction.oncomplete = () => {
        resolve("Dados importados com sucesso!");
      };
    };
  });
};

export const synchronizeDataIndexDB = async (userIdLogged: string) => {
  try {
    const db = await getDB();
    const tx = db.transaction([PUBLIC_KEY_STORE, KEY_STORE, MESSAGE_STORE], "readonly");

    const [publicKeys, privateKeys, userData] = await Promise.all([
      tx.objectStore(PUBLIC_KEY_STORE).getAll(),
      tx.objectStore(KEY_STORE).getAll(),
      tx.objectStore(MESSAGE_STORE).get(userIdLogged),
    ]);

    await tx.done;

    const userIds = new Set<string>();

    publicKeys.forEach((key) => userIds.add(key.userId));
    privateKeys.forEach((key) => userIds.add(key.userId));

    if (userData?.messages) {
      Object.keys(userData.messages).forEach((senderId) => userIds.add(senderId));
    }

    const syncPromises = Array.from(userIds).map(async (userId) => {
      try {
        await api.get(`/graphic/${userId}`);
      } catch (error) {
        await deleteAllInformationsFromUserId(userIdLogged, userId);
      }
    });

    await Promise.all(syncPromises);
  } catch (error) {
    console.error("Erro ao sincronizar dados do IndexedDB:", error);
  }
};

export const limparTodosDadosIndexedDB = (dbName: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName);

    request.onerror = () => {
      reject(`Erro ao abrir o banco: ${request.error}`);
    };

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(db.objectStoreNames, "readwrite");

      transaction.onerror = () => {
        reject(`Erro na transação: ${transaction.error}`);
      };

      for (const storeName of db.objectStoreNames) {
        const store = transaction.objectStore(storeName);
        store.clear();
      }

      transaction.oncomplete = () => {
        resolve("Todos os dados do IndexedDB foram apagados com sucesso!");
      };
    };
  });
};

// Carregar mensagens
export const loadMessagesFromIndexedDB = async (userId: string): Promise<{ [chatId: string]: Message[] }> => {
  try {
    const db = await getDB();
    const store = db.transaction(MESSAGE_STORE).objectStore(MESSAGE_STORE);

    const allMessages = await store.get(userId);
    return allMessages?.messages || {};
  } catch (error) {
    console.error("Erro ao carregar mensagens do IndexedDB:", error);
    return {};
  }
};

// Armazenar mensagens enquanto offline
export const storeMessage = async (message: any) => {
  try {
    const db = await getDB();
    await db.put(MESSAGE_STORE, message);
  } catch (error) {
    console.error("Erro ao armazenar mensagem no IndexedDB", error);
  }
};

// Verificar se o usuário está offline
export const checkIfUserIsOffline = (receiverId: string): boolean => {
  return !navigator.onLine;
};

// Monitorar mudanças na conexão de rede
export const monitorConnection = (onOnline: () => void, onOffline: () => void) => {
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
};

// Toggle functions
export const saveToggleState = async (key: string, state: boolean): Promise<void> => {
  try {
    const db = await getDB();
    await db.put("toggles", {
      key,
      state,
      updatedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.error(`Falha ao salvar toggle ${key}:`, error);
    throw error;
  }
};

export const loadToggleState = async (key: string): Promise<boolean> => {
  try {
    const db = await getDB();
    const result = await db.get("toggles", key);
    return result?.state ?? false;
  } catch (error) {
    console.error(`Falha ao carregar toggle ${key}:`, error);
    return false;
  }
};

export const updateMessageDurationInDB = async (groupId: string, duration: number | null) => {
  try {
    const db = await getDB();
    const group = await db.get("groups", groupId);

    if (group) {
      await db.put("groups", {
        ...group,
        messageDuration: duration,
      });
    }
  } catch (error) {
    console.error("Erro ao atualizar duração da mensagem:", error);
    throw error;
  }
};
